import type { ILoggedUser } from "@/entities/auth/types.ts"
import type { CustomRoute } from "@/shared/types"
import { tCommon } from "../config/i18n"
import { EnergyAudit } from "@/features/energy-audit/types"

export function withTransition(callback: () => void) {
  if ("startViewTransition" in document) {
    document.startViewTransition(() => {
      callback()
    })
  } else {
    callback()
  }
}

export function formatRemainingTime(targetDate: Date | string): string | null {
  const now = new Date()
  const target = new Date(targetDate)

  if (Number.isNaN(target.getTime()) || target.getTime() < now.getTime()) return null

  const diffMs = target.getTime() - now.getTime()

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((diffMs / (1000 * 60)) % 60)

  return `${days} ${tCommon("days-mini")} ${hours} ${tCommon("hours-mini")} ${minutes} ${tCommon("minutes-mini")}`
}

export function showQuarterNumber(quarterNumber: number) {
  if (quarterNumber === 1) return "I"
  if (quarterNumber === 2) return "II"
  if (quarterNumber === 3) return "III"
  if (quarterNumber === 4) return "IV"
  return ""
}

export function generateTempId(prefix = "temp"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 8)}`
}

// Auth helpers
export function hasAllowedRole(route: CustomRoute, user: ILoggedUser) {
  return route.handle?.allowedRoles ? route.handle.allowedRoles.includes(user.base_role) : true
}

export function hasDisallowedRole(route: CustomRoute, user: ILoggedUser) {
  return route.handle?.disallowedRoles ? route.handle.disallowedRoles.some(p => user.base_role.includes(p)) : false
}

export function hasAllowedPermissions(route: CustomRoute, user: ILoggedUser) {
  return route.handle?.allowedPermissions ? route.handle.allowedPermissions.some(p => user?.permissions?.includes(p)) : true
}

export function isAllowedRoute(route: CustomRoute, user: ILoggedUser) {
  return hasAllowedRole(route, user) && hasAllowedPermissions(route, user) && !hasDisallowedRole(route, user)
}

export function filterAllowedRoutes(route: CustomRoute, user: ILoggedUser): CustomRoute | null {
  if (isAllowedRoute(route, user)) {
    if (route.children?.length) {
      return {
        ...route,
        children: route.children.filter(route => filterAllowedRoutes(route, user))
      }
    }
    return route
  }
  return null
}

export const getFilesFromEnergyAudit = (audit: EnergyAudit) => {
  const files = []
  if (audit.contract) {
    if (audit.contract?.contract_file) {
      files.push({ url: audit.contract?.contract_file, filename: tCommon("file-names.contract-file") })
    }
    if (audit.contract?.tz_file) {
      files.push({ url: audit.contract?.tz_file, filename: tCommon("file-names.technical-task-file") })
    }
  }
  if (audit.audit_process) {
    if (audit.audit_process?.report_file) {
      files.push({ url: audit.audit_process?.report_file, filename: tCommon("file-names.report-file") })
    }
    if (audit.audit_process?.auditor_letter_file) {
      files.push({ url: audit.audit_process?.auditor_letter_file, filename: tCommon("file-names.audit-process-letter-file") })
    }
    if (audit.audit_process?.rejection_letter_file) {
      files.push({ url: audit.audit_process?.rejection_letter_file, filename: tCommon("file-names.audit-process-rejection-letter-file") })
    }
    if (audit.audit_process?.rejection_letter_file) {
      files.push({ url: audit.audit_process?.rejection_letter_file, filename: tCommon("file-names.audit-process-report-file") })
    }
  }
  if (audit.program) {
    if (audit.program?.report_file) {
      files.push({ url: audit.program?.report_file, filename: tCommon("file-names.report-file-to-program") })
    }
    if (audit.program?.file) {
      files.push({ url: audit.program?.file, filename: tCommon("file-names.program-file") })
    }
  }
  return files
}
